import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { Net2BeepleMethodsEnum } from '../enums/net2beeple-methods.enum';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { HttpService } from '../http/http.service';
import { CompanyUser } from './user.types';
import { UserUtils } from './user.utils';

@Injectable({providedIn: 'root'})
export class UserService
{
    private httpService = inject(HttpService);
    private userUtils = inject(UserUtils);
    private _user: BehaviorSubject<CompanyUser> = new BehaviorSubject<CompanyUser>(undefined);

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: CompanyUser) {
        localStorage.setItem(StorageKeysEnum.user, JSON.stringify(value));
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<CompanyUser> {
        return this._user.asObservable();
    }

    /**
     * Get the current logged in user data
     */
     get(): Observable<CompanyUser> {
        let user = this._user.getValue();
        if (!user) {
            user = JSON.parse(localStorage.getItem(StorageKeysEnum.user)) as CompanyUser;
            this.user = user;
        }
        if (!user.profileImage) {
            this.userUtils.getRandomAnimalAvatar().then((image) => {
                user.profileImage = image;
                firstValueFrom(this.update(user)).then((newUser) => {
                    if (!!newUser) {
                        this.user = newUser;
                        return of(newUser);
                    } else {
                        return of(user);
                    }
                });
            });
        }
        return of(user);
    }

    /**
     * Update the user
     *
     * @param user
     */
     update(companyUser: CompanyUser): Observable<CompanyUser> {
        return this.httpService.post<any>(Net2BeepleMethodsEnum.flushCompanyUser, { companyUser }).pipe(
            map((response) => {
                if (response && response.companyUser) {
                    this._user.next(response.companyUser);
                    localStorage.setItem(StorageKeysEnum.user, JSON.stringify(response.companyUser));
                    return response.companyUser;
                }
                return undefined;
            })
        );
    }

    delete() {
        localStorage.removeItem(StorageKeysEnum.user);
    }

    /**
     * Search users from a company
     *
     * @param expr
     * @param companyId
     */
     searchCompanyUser(expr: string, companyId: string): Observable<CompanyUser[]> {
        return this.httpService.post(Net2BeepleMethodsEnum.searchCompanyUserList, {
            expr,
            company: companyId
        }).pipe(
            map((response) => {
                if (response && response.companyUser) {
                    return response.companyUser;
                }
                return [];
            })
        );
    }

    retrieveCompanyUser(companyId: string, orderBy?: string) {
        return this.httpService.post(Net2BeepleMethodsEnum.retrieveCompanyUserList, {
            companyId,
            orderBy,
            chunkSize: 0
        }).pipe(
            map((response) => {
                if (response && response.companyUser) {
                    return response.companyUser;
                }
                return [];
            })
        );
    }
}
